
import { useState, useEffect } from 'react';
import React from 'react';

export default function Home() {
  const [isVisible, setIsVisible] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData(e.currentTarget);
      const response = await fetch('https://readdy.ai/api/form/d44d3spha0trr1haa230', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitStatus('success');
      setShowContactForm(false);
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-100 border-b border-gray-300 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/7fa9f84865a64340db806519958fd756/81010e82b868ec313cabce239a417c75.jpeg"
                alt="Serviti Corps Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">SERVITIUM</h1>
              <p className="text-gray-600 text-sm">
                Preparing Communities • Preserving Connection
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="min-h-screen flex items-center justify-center px-6 relative"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/7fa9f84865a64340db806519958fd756/c24700c37e2ce0d0f004cb2503370130.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40"></div>
        <div
          className={`text-center max-w-4xl transition-all duration-1000 relative z-10 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            The Storm Is Here.{' '}
            <span className="bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
              We're Building the Shelter.
            </span>
          </h1>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Servitium is the workforce, the fraternal order, and the institutional
            infrastructure preparing communities to survive the AI transition —
            and come out the other side stronger.
          </p>
          <button
            onClick={() => setShowContactForm(true)}
            className="bg-gray-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-600 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 whitespace-nowrap cursor-pointer"
          >
            Learn More
          </button>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-gray-50 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <div className="text-6xl font-bold text-gray-800 mb-4">2015</div>
              <p className="text-gray-600 text-lg">The year we started warning</p>
            </div>
            <div>
              <div className="text-6xl font-bold text-gray-700 mb-4">0</div>
              <p className="text-gray-600 text-lg">
                Communities with a structural preparation plan
              </p>
            </div>
            <div>
              <div className="text-6xl font-bold text-gray-800 mb-4">2025</div>
              <p className="text-gray-600 text-lg">
                The year the acceleration started, exactly as predicted
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Crisis Section */}
      <section className="bg-gray-800 py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            The Crisis No One Is Preparing For
          </h2>
          <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
            <p>
              AI will not arrive as a single event. It will roll through industries,
              communities, and families at different speeds, in different ways,
              for years. Manufacturing. Logistics. Transportation. Customer service.
              Legal. Accounting. White collar and blue collar. Rural and urban. No
              community is exempt.
            </p>
            <p>
              Economic development agencies know how to attract new business. None
              have a plan for preserving the economic life of communities that
              already exist. The jobs they recruited last decade are being
              automated right now. The tax base they built is eroding. The
              downtowns they revitalized are about to hollow out — not over
              decades, as with deindustrialization, but within years.
            </p>
            <p>
              Servitium exists because this crisis demands more than policy papers
              and panel discussions. It demands infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* Serviti Corps Section */}
      <section className="bg-gray-100 py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
            Serviti Corps — The First Responders
          </h2>
          <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
            <p>
              The AI transition will arrive as a storm — rolling through industries,
              communities, and families at different speeds, in different ways,
              for years. Most communities have no plan. No infrastructure. No one
              trained to help. Serviti Corps is the permanent workforce built to
              weather that storm — a mix of paid professionals and trained
              volunteers, retrained not to compete with machines but to do what
              machines cannot: hold communities together and bring them through
              the other side stronger than before.
            </p>
            <p>
              A laid-off factory supervisor becomes a transition counselor for the
              plant that just automated his shift. A former truck driver becomes the
              person helping elderly residents navigate a world that changed faster
              than anyone promised. A concerned mother volunteers as a block leader,
              guiding fellow parents through uncertainty they shouldn't have to
              face alone. A pastor shepherds his congregation through profound
              economic loss with real resources behind him instead of just prayers.
              A mayor watches data centers arrive to train the AI that will displace
              her workers — and for the first time has a framework to turn that
              infrastructure into community resilience.
            </p>
            <p>
              The people closest to the pain become the first responders for the AI
              age — not victims waiting for rescue, but organized, trained, and
              ready to lead their neighbors through the transition and into what
              comes next.
            </p>
          </div>
        </div>
      </section>

      {/* Order of Servitium Section */}
      <section className="bg-gray-800 py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-white mb-8 text-center">
            The Order of Servitium
          </h2>
          <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
            <p>
              Every revolution in history has been seeded by the same thing: men
              with no purpose and no upward mobility. The French Revolution. The
              Bolshevik Revolution. The rise of fascism across Europe. When men
              lose their economic identity and see no path forward, they don't
              quietly disappear. They get angry. And angry men without structure
              become dangerous men.
            </p>
            <p>
              AI is creating this at a scale we've never seen — and the algorithms
              are pouring gasoline on it. The same technology displacing men from
              their jobs is feeding them ragebait designed to channel their
              frustration into tribalism, resentment, and violence. Thirty minutes
              on a smartphone can radicalize a young man who just lost his purpose.
              The manosphere, the extremist pipelines, the political rage machines
              — they are all recruiting from the same pool: men with nothing to do,
              nowhere to belong, and no one holding them accountable.
            </p>
            <p>
              In 1099, the Knights of Malta were founded in Jerusalem to protect
              pilgrims and care for the sick — and nine centuries later, they still
              operate in 120 countries. In 1869, the Knights of Labor organized in
              Philadelphia to defend workers being crushed by industrialization,
              becoming America's first national labor union with nearly a million
              members. In 1882, the Knights of Columbus were founded in New Haven
              to protect immigrant Catholic families excluded from every other
              institution that might have helped them. Every great disruption in
              history has produced an order of men who swore oaths, bound
              themselves to a mission, and built the infrastructure that carried
              civilization through the transition.
            </p>
            <p>
              The Order of Servitium is the answer for this one — a modern
              fraternal order forging men of restraint, discipline, and brotherhood.
              Knights are trained to understand AI — not just what it can do, but
              what it is doing to us: how algorithms manipulate attention, how
              automation reshapes economies, and how to wield these tools for the
              protection of society rather than be wielded by them. Men holding
              each other accountable. Men who swear off ragebait the way medieval
              knights swore off brigandage. Men who protect the innocent instead of
              falling into rage. Men who channel their strength into service — not
              destruction. The most powerful technology ever created demands the
              most disciplined, most informed men ever formed.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-700 py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-8">The Window Is Closing</h2>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed">
            Soon, AI will accelerate beyond our ability to respond. We have a narrow
            window to build the infrastructure for human-AI collaboration that
            preserves dignity, purpose, and community resilience.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => setShowContactForm(true)}
              className="bg-gray-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-500 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 whitespace-nowrap cursor-pointer"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-400">
            Servitium 2026. All rights reserved. |{' '}
            <a
              href="mailto:john@servitiumai.ai"
              className="text-gray-500 hover:text-gray-400 transition-colors cursor-pointer"
            >
              john@servitiumai.ai
            </a>
          </p>
          <div className="mt-4">
            <a
              href="https://readdy.ai/?ref=logo"
              className="text-gray-500 hover:text-gray-400 transition-colors text-sm cursor-pointer"
            >
              Website Builder
            </a>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Learn More</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close modal"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form
              id="contact-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d44d3spha0trr1haa230"
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Organization
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              <div>
                <label
                  htmlFor="interests"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  What interests you most?
                </label>
                <textarea
                  id="interests"
                  name="interests"
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Maximum 500 characters
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-slate-800 text-white px-4 py-2 rounded-md hover:bg-slate-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>

            {submitStatus && (
              <div
                className={`mt-4 p-3 rounded-md ${
                  submitStatus === 'success'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {submitStatus === 'success'
                  ? 'Thank you! Your information has been submitted successfully.'
                  : 'There was an error submitting your information. Please try again.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
